import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { OnboardingRoutes } from '.';
import { ProductMasterDataInput } from '../../../types/product_master_data';
import { OnboardingLS } from '../../../util/local_storage/onboarding.ls';
import OnboardingAbortModal from './modals/OnboardingAbortModal';
import { OnboardingData } from './model/OnboardingData';

import {
  OnboardingStockLocationAddPane,
  OnboardingCompanyPane,
  OnboardingStockLocationEditPane,
  OnboardingReferenceDataImportPane,
  OnboardingReferenceDataOverviewPane,
  OnboardingStockImportPane,
  OnboardingUserPane,
  OnboardingStockLocationOverviewPane,
  OnboardingStockLocationImportPane,
  OnboardingReferenceDataAddPane,
  OnboardingReferenceDataEditPane,
} from './panes';

export default function OnboardingScreen() {
  const navigate = useNavigate();
  const [loadAttempted, setLoadAttempted] = useState(false);

  const [data, _setData] = useState<OnboardingData>(new OnboardingData());
  const setData = useMemo(() => {
    function update(data: OnboardingData) {
      _setData(data.update());
    }
    return update;
  }, [_setData]);
  const [abortModalOpen, setAbortModalOpen] = useState(false);

  useEffect(() => {
    const data = OnboardingLS.getOnboardingData();
    if (data) setData(data);
    setLoadAttempted(true);
  }, []);

  useEffect(() => {
    if (loadAttempted) OnboardingLS.setOnboardingData(data);
  }, [data]);

  return (
    <>
      {abortModalOpen && (
        <OnboardingAbortModal
          open={abortModalOpen}
          setOpen={setAbortModalOpen}
          onAbort={() => {
            OnboardingLS.removeOnboardingData();
            navigate('/company');
          }}
        />
      )}

      <div className='flex flex-grow flex-col items-center justify-center bg-gray-25'>
        <div className='flex h-[720px] w-[782px] flex-col gap-4 rounded-4 border border-solid border-gray-300 bg-white px-12 py-6'>
          <Routes>
            <Route path={'/'} element={<Navigate to={OnboardingRoutes.company} replace />} />
            <Route path={'/general'} element={<OnboardingCompanyPane data={data} setData={setData} />} />

            {data.company.name || !loadAttempted ? (
              <>
                <Route path={'/stock_locations/*'} element={<Navigate to={OnboardingRoutes.stockLocationImport} />} />
                <Route
                  path={'/stock_locations/overview'}
                  element={<OnboardingStockLocationOverviewPane data={data} setData={setData} />}
                />
                <Route
                  path={'/stock_locations/import'}
                  element={<OnboardingStockLocationImportPane data={data} setData={setData} />}
                />
                <Route
                  path={'/stock_locations/add'}
                  element={<OnboardingStockLocationAddPane data={data} setData={setData} companyId={data.company.id} />}
                />
                <Route
                  path={'/stock_locations/:id/edit'}
                  element={
                    <OnboardingStockLocationEditPane
                      data={data}
                      onSave={stockLocation => {
                        data.manual.stockLocations.set(stockLocation.id, stockLocation);
                        setData(data);
                      }}
                    />
                  }
                />
              </>
            ) : (
              <Route path={'/stock/*'} element={<Navigate to={OnboardingRoutes.company} />} />
            )}

            {data.summary.stockLocations.size || !loadAttempted ? (
              <>
                <Route path={'/reference_data/*'} element={<Navigate to={OnboardingRoutes.referenceDataOverview} />} />
                <Route
                  path={'/reference_data/overview'}
                  element={<OnboardingReferenceDataOverviewPane data={data} />}
                />
                <Route
                  path={'/reference_data/import'}
                  element={<OnboardingReferenceDataImportPane data={data} setData={setData} />}
                />
                <Route
                  path={'/reference_data/add'}
                  element={<OnboardingReferenceDataAddPane data={data} setData={setData} />}
                />
                <Route
                  path={'/reference_data/:id/edit'}
                  element={
                    <OnboardingReferenceDataEditPane
                      data={data}
                      onSave={pmd => {
                        data.manual.productMasterData.set(pmd.id, new ProductMasterDataInput(pmd));
                        setData(data);
                      }}
                    />
                  }
                />
              </>
            ) : (
              <Route path={'/reference_data/*'} element={<Navigate to={OnboardingRoutes.stockLocationImport} />} />
            )}

            {data.summary.stockLocations.size || !loadAttempted ? (
              <>
                <Route path={'/stock/*'} element={<Navigate to={OnboardingRoutes.stockImport} />} />
                <Route path={'/stock/import'} element={<OnboardingStockImportPane data={data} setData={setData} />} />
              </>
            ) : (
              <Route path={'/stock/*'} element={<Navigate to={OnboardingRoutes.stockLocationImport} />} />
            )}

            <Route path={'/users'} element={<OnboardingUserPane data={data} setData={setData} />} />
          </Routes>
        </div>
      </div>
    </>
  );
}
